/**
 * Rendering for markdown a *user* typed -- a remark, a note, a comment -- as opposed to the markdown
 * we author ourselves in a manifest (an Instructions block) or ship in a file.
 *
 * Two differences from a plain `marked.parse()`:
 *
 * - raw HTML in the source is shown as literal text instead of being parsed as markup, so a remark
 *   cannot put a script tag or an event handler into the page of whoever reads it -- including the
 *   text after an inline `<kbd>` / `<code>` / `<pre>` / `<script>` tag, which marked passes through
 *   unescaped (see walkTokens below);
 * - links are limited to http(s) / mailto / tel / relative targets (not protocol-relative `//host`),
 *   and open in a new tab so a reader is not navigated away from a form they are part way through
 *   filling in;
 * - an image is drawn only from an absolute http(s) URL on someone else's host. An `<img>` fires a GET
 *   the moment the remark renders, with the reader's session cookie when it points at the page's own
 *   host or another Headlight host -- `![x](/1.0/Authentication/Impersonate/123)` would log a
 *   super-user out of their own session just by opening the ticket. Any other image is shown as a
 *   link labelled with its alt text (when the link rules allow it) or as its alt text.
 *
 * marked does none of this on its own: since v5 it has no sanitizer, and its `cleanUrl()` only encodes
 * a URI -- it does not reject `javascript:`.
 *
 * This lives beside the providers (and is deep-required by the web apps the same way
 * `_VocabularySelectorMap.js` is) so every screen that shows a remark renders it identically.
 */
// report_definitions reports/HeadLight-ATM/headlight-document/source/atm-user-markdown.js carries a copy of these rules for the ATM report -- change both together.

const libMarked = require('marked');

/** @type {Record<string, string>} */
const _HTMLEntityMap = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#039;' };

/** Protocols a user-authored remark is allowed to link to. */
const _SafeLinkProtocolPattern = /^(?:https?:\/\/|mailto:|tel:|\/|\.{1,2}\/|#)/i;
/**
 * A protocol-relative href (`//host/...`; browsers read `\` as `/` in an http(s) URL, so `/\host` is
 * one too). It names another host while looking like a relative path, so it is never a link or image.
 */
const _ProtocolRelativePattern = /^[/\\]{2}/;
/** The only hrefs an `<img>` may load: absolute http(s). Relative paths are the page's own origin. */
const _AbsoluteImageURLPattern = /^https?:\/\//i;
/** Browsers drop ASCII tab and newline anywhere in a URL, so `/\t/host` is `//host` by the time it loads. */
const _URLWhitespacePattern = /[\t\n\r]/g;

/** @type {Record<string, string>} */
const _TextEntityMap = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;' };
/** Unescaped specials, leaving existing entity references alone (so applying it twice is harmless). */
const _TextEscapePattern = /[<>"']|&(?!(?:#\d{1,7}|#[Xx][a-fA-F0-9]{1,6}|\w+);)/g;

/**
 * Escapes a value for use as HTML text or as an attribute value.
 *
 * @param {any} pValue - the value to escape.
 * @return {String} - the escaped value, or an empty string for null/undefined.
 */
function escapeHTML(pValue)
{
	if ((pValue === null) || (typeof(pValue) === 'undefined'))
	{
		return '';
	}
	return String(pValue).replace(/[&<>"']/g, (pCharacter) => _HTMLEntityMap[pCharacter]);
}

/**
 * Escapes text that marked may already have escaped, without double-escaping its entities -- the same
 * rule marked uses for text, so ordinary text comes out exactly as it did.
 *
 * @param {any} pText - text from a marked token.
 * @return {String} - safe HTML text.
 */
function escapeTextIdempotent(pText)
{
	if ((pText === null) || (typeof(pText) === 'undefined'))
	{
		return '';
	}
	return String(pText).replace(_TextEscapePattern, (pCharacter) => _TextEntityMap[pCharacter]);
}

/**
 * The href as a browser will read it: trimmed, with the tabs and newlines a browser drops removed.
 *
 * @param {any} pHref - the href from the markdown source.
 * @return {String}
 */
function _normalizeHref(pHref)
{
	return (typeof(pHref) === 'string') ? pHref.replace(_URLWhitespacePattern, '').trim() : '';
}

/**
 * Returns the trimmed href when it points somewhere a remark is allowed to link to, otherwise null.
 *
 * @param {any} pHref - the href from the markdown source.
 * @return {String|null}
 */
function safeURL(pHref)
{
	const tmpHref = _normalizeHref(pHref);
	if (_ProtocolRelativePattern.test(tmpHref))
	{
		return null;
	}
	return _SafeLinkProtocolPattern.test(tmpHref) ? tmpHref : null;
}

/**
 * The host the page is served from, read at call time; '' where there is no page (node, tests).
 *
 * @return {String} - the lower-cased hostname without a trailing dot.
 */
function _currentHostname()
{
	try
	{
		const tmpLocation = /** @type {any} */ (globalThis).location;
		const tmpHostname = (tmpLocation && (typeof(tmpLocation.hostname) === 'string')) ? tmpLocation.hostname : '';
		return tmpHostname.toLowerCase().replace(/\.$/, '');
	}
	catch (pError)
	{
		return '';
	}
}

/**
 * Returns the URL to draw when an image may be loaded the moment a remark renders, otherwise null.
 *
 * Only an absolute http(s) URL with no credentials, on a host that is not the page's own, not a
 * Headlight host (`/1.0/*` there runs with the reader's session), not localhost and not an IP literal
 * (a reader's browser reaching into their own network). Relative and protocol-relative paths are the
 * page's own origin, where the API lives, so they never load.
 *
 * @param {any} pHref - the image href from the markdown source.
 * @return {String|null}
 */
function safeImageURL(pHref)
{
	const tmpHref = _normalizeHref(pHref);
	if (!_AbsoluteImageURLPattern.test(tmpHref) || (typeof(URL) !== 'function'))
	{
		return null;
	}
	let tmpURL;
	try
	{
		tmpURL = new URL(tmpHref);
	}
	catch (pError)
	{
		return null;
	}
	if (((tmpURL.protocol !== 'https:') && (tmpURL.protocol !== 'http:')) || tmpURL.username || tmpURL.password)
	{
		return null;
	}
	const tmpHostname = tmpURL.hostname.toLowerCase().replace(/\.+$/, '');
	if (!tmpHostname)
	{
		return null;
	}
	// The URL parser has already turned every IPv4 spelling (hex, octal, a bare number) into a dotted
	// quad, and a host whose last label is all digits is never a name.
	const tmpLastLabel = tmpHostname.split('.').pop() || '';
	if ((tmpHostname.charAt(0) === '[') || /^(?:0x[0-9a-f]*|\d+)$/i.test(tmpLastLabel))
	{
		return null;
	}
	if ((tmpHostname === 'localhost') || /\.localhost$/.test(tmpHostname))
	{
		return null;
	}
	if ((tmpHostname === 'headlight.com') || /\.headlight\.com$/.test(tmpHostname))
	{
		return null;
	}
	const tmpCurrentHostname = _currentHostname();
	if (tmpCurrentHostname && (tmpHostname === tmpCurrentHostname))
	{
		return null;
	}
	return tmpURL.href;
}

// Styling for a rendered remark. Registered with the pict CSS map rather than an app stylesheet so
// every app that loads this provider gets it without a per-app css copy (there are ten copies of
// headlight-panels.css in the tree already).
const RemarksCSS = /*css*/`
.hl-md-remarks.content > :last-child { margin-bottom: 0; }
.hl-md-remarks.content p { margin-bottom: 0.5em; }
.hl-md-remarks.content h1,
.hl-md-remarks.content h2,
.hl-md-remarks.content h3,
.hl-md-remarks.content h4,
.hl-md-remarks.content h5,
.hl-md-remarks.content h6 { font-size: 1.1em; margin-top: 0; margin-bottom: 0.25em; color: inherit; }
.hl-md-remarks.content ul,
.hl-md-remarks.content ol { margin-top: 0.25em; margin-bottom: 0.5em; }
.hl-md-remarks.content table { margin-top: 0.25em; margin-bottom: 0.5em; }
.hl-md-remarks.content img { max-width: 100%; }
/* A remark also sits on dark surfaces (the sample panel), so headings, bold text and quotes take the text color around them rather than Bulma's fixed dark ones. */
.hl-md-remarks.content strong { color: inherit; }
.hl-md-remarks.content blockquote
{
	background-color: transparent;
	color: inherit;
	border-left: 3px solid currentColor;
	border-left-color: color-mix(in srgb, currentColor 35%, transparent);
	padding: 0.25em 0 0.25em 0.75em;
	margin-bottom: 0.5em;
}
.hl-md-remarks-collapse summary { display: block; list-style: none; }
.hl-md-remarks-collapse summary::-webkit-details-marker { display: none; }
.hl-md-remarks-collapse:not([open]) .hl-md-remarks-text
{
	display: -webkit-box;
	-webkit-line-clamp: 3;
	-webkit-box-orient: vertical;
	overflow: hidden;
}
.hl-md-remarks-collapse.is-truncated summary { cursor: pointer; }
.hl-md-remarks-collapse.is-truncated summary::after
{
	content: "Read more";
	display: block;
	margin-top: 0.25em;
	font-size: 0.85em;
	color: var(--bulma-link, #3273dc);
}
.hl-md-remarks-collapse.is-truncated[open] summary::after { content: "Read less"; }
`;

const _CollapseSelector = '.hl-md-remarks-collapse';
const _CollapseTextSelector = '.hl-md-remarks-collapse .hl-md-remarks-text';
/** Set on a collapsible remark whose text runs past the clamp; it is what shows Read more. */
const _TruncatedClass = 'is-truncated';
/** @type {WeakSet<any>} */
const _WatchedDocuments = new WeakSet();

/**
 * Marks a collapsible remark as truncated when its clamped text runs past the clamp, so Read more
 * shows only when there is more to read.
 *
 * @param {any} pCollapse - the `details.hl-md-remarks-collapse` element.
 * @return {Boolean} - whether the remark is marked truncated afterwards.
 */
function updateRemarkTruncation(pCollapse)
{
	if (!pCollapse || !pCollapse.classList)
	{
		return false;
	}
	// An open remark is unclamped and measures as fitting, so it keeps its mark until it closes.
	if (pCollapse.open)
	{
		return pCollapse.classList.contains(_TruncatedClass);
	}
	const tmpText = pCollapse.querySelector('.hl-md-remarks-text');
	// A remark on a tab that isn't showing has no layout yet; it is measured once it gets one.
	if (!tmpText || (tmpText.clientHeight === 0))
	{
		return pCollapse.classList.contains(_TruncatedClass);
	}
	const tmpTruncated = ((tmpText.scrollHeight - tmpText.clientHeight) > 1);
	pCollapse.classList.toggle(_TruncatedClass, tmpTruncated);
	return tmpTruncated;
}

/**
 * The clamped remark text elements in and under a node.
 *
 * @param {any} pNode - a DOM node.
 * @return {Array<any>}
 */
function getRemarkTextElements(pNode)
{
	if (!pNode || (pNode.nodeType !== 1))
	{
		return [];
	}
	const tmpElements = Array.from(pNode.querySelectorAll(_CollapseTextSelector));
	if (pNode.matches(_CollapseTextSelector))
	{
		tmpElements.push(pNode);
	}
	return tmpElements;
}

/**
 * Keeps every collapsible remark in a document marked truncated or not as it renders, resizes or
 * goes from hidden to shown, and stops a click from opening a remark that already fits. Safe to
 * call more than once; does nothing where there is no DOM.
 *
 * @param {any} [pDocument] - the document to watch; defaults to the global one.
 * @return {Boolean} - true when the document is being watched.
 */
function watchRemarksTruncation(pDocument)
{
	const tmpDocument = pDocument || ((typeof(document) !== 'undefined') ? document : null);
	const tmpWindow = tmpDocument ? tmpDocument.defaultView : null;
	if (!tmpWindow || (typeof(tmpWindow.ResizeObserver) !== 'function') || (typeof(tmpWindow.MutationObserver) !== 'function'))
	{
		return false;
	}
	if (_WatchedDocuments.has(tmpDocument))
	{
		return true;
	}
	_WatchedDocuments.add(tmpDocument);

	const tmpResizeObserver = new tmpWindow.ResizeObserver((pEntries) =>
	{
		for (const tmpEntry of pEntries)
		{
			updateRemarkTruncation(tmpEntry.target.closest(_CollapseSelector));
		}
	});
	const tmpMutationObserver = new tmpWindow.MutationObserver((pRecords) =>
	{
		for (const tmpRecord of pRecords)
		{
			for (const tmpNode of Array.from(tmpRecord.removedNodes))
			{
				for (const tmpText of getRemarkTextElements(tmpNode))
				{
					tmpResizeObserver.unobserve(tmpText);
				}
			}
			for (const tmpNode of Array.from(tmpRecord.addedNodes))
			{
				for (const tmpText of getRemarkTextElements(tmpNode))
				{
					tmpResizeObserver.observe(tmpText);
				}
			}
		}
	});
	tmpMutationObserver.observe(tmpDocument.documentElement, { childList: true, subtree: true });
	for (const tmpText of getRemarkTextElements(tmpDocument.documentElement))
	{
		tmpResizeObserver.observe(tmpText);
	}

	tmpDocument.addEventListener('click', (pEvent) =>
	{
		const tmpTarget = pEvent.target;
		if (!tmpTarget || (typeof(tmpTarget.closest) !== 'function') || tmpTarget.closest('a'))
		{
			return;
		}
		const tmpSummary = tmpTarget.closest(`${ _CollapseSelector } > summary`);
		if (tmpSummary && !tmpSummary.parentElement.open && !tmpSummary.parentElement.classList.contains(_TruncatedClass))
		{
			pEvent.preventDefault();
		}
	});
	return true;
}

/** @type {any} Built on first use; marked instances are reusable and stateless across parses. */
let _UserContentMarked = null;
/** So an unusable marked is reported once, not once per remark on the page. */
let _MarkedUnusableReported = false;
/** How many links the render is inside, so an image in link text does not become a link in a link. */
let _LinkDepth = 0;

/**
 * The hardened marked instance.
 *
 * @return {any}
 */
function getUserContentMarked()
{
	if (_UserContentMarked)
	{
		return _UserContentMarked;
	}
	if (typeof(libMarked.Marked) !== 'function')
	{
		// The Marked class arrived in marked 5, and the renderer overrides below are the object-argument
		// form from marked 13+. An older marked resolves here silently -- it has neither -- and every
		// remark then falls back to its escaped source, which looks like the feature simply not working.
		// marked is a transitive dependency in several places and no lockfile is committed, so the
		// version a build resolves is whatever hoisting produced: say so by name rather than throwing a
		// bare TypeError per remark.
		throw new Error('markdown remarks need marked 13 or newer (no Marked class found on the resolved marked); declare "marked": "^14.1.2" in the package this file resolves from');
	}
	_UserContentMarked = new libMarked.Marked(
		{
			gfm: true,
			// A remark is typed into a textarea or a markdown editor, where pressing Enter means a
			// line break. Sample notes in particular arrive as labelled lines ("Producer Supplier
			// Code: ...\nPlant ID: ..."), which markdown's default paragraph folding would run
			// together into one line -- worse than the raw text it replaces.
			breaks: true,
			// After an inline <pre> / <code> / <kbd> / <script> tag marked is in its "raw block" state,
			// and the text up to the closing tag reaches the output unescaped -- so
			// `<kbd><img/src=x onerror=...></kbd>` (an <img> its tag pattern does not recognise as HTML)
			// would run. Escape every leaf text token; text marked already escaped is left as it is.
			walkTokens: function(/** @type {any} */ pToken)
			{
				if (pToken && (pToken.type === 'text') && !pToken.tokens && (typeof(pToken.text) === 'string'))
				{
					pToken.text = escapeTextIdempotent(pToken.text);
				}
			},
			renderer:
			{
				// `this` here is marked's renderer, so these have to be function expressions to
				// reach `this.parser`.
				html: function(pToken)
				{
					return escapeHTML(pToken.text);
				},
				link: function(pToken)
				{
					let tmpText = '';
					_LinkDepth++;
					try
					{
						tmpText = this.parser.parseInline(pToken.tokens);
					}
					finally
					{
						_LinkDepth--;
					}
					const tmpHref = safeURL(pToken.href);
					if (!tmpHref)
					{
						return tmpText;
					}
					const tmpTitle = pToken.title ? ` title="${ escapeHTML(pToken.title) }"` : '';
					return `<a href="${ escapeHTML(tmpHref) }"${ tmpTitle } target="_blank" rel="noopener noreferrer">${ tmpText }</a>`;
				},
				image: function(pToken)
				{
					// marked hands over the alt text already escaped.
					const tmpAlt = escapeTextIdempotent(pToken.text);
					const tmpTitle = pToken.title ? ` title="${ escapeHTML(pToken.title) }"` : '';
					const tmpSource = safeImageURL(pToken.href);
					if (tmpSource)
					{
						return `<img class="image" src="${ escapeHTML(tmpSource) }" alt="${ tmpAlt }"${ tmpTitle }>`;
					}
					// Not drawn: offer it as a link the reader chooses to follow, unless it is already inside one.
					const tmpHref = (_LinkDepth > 0) ? null : safeURL(pToken.href);
					if (!tmpHref)
					{
						return tmpAlt;
					}
					return `<a href="${ escapeHTML(tmpHref) }"${ tmpTitle } target="_blank" rel="noopener noreferrer">${ tmpAlt.trim() ? tmpAlt : 'image' }</a>`;
				},
			},
		});
	return _UserContentMarked;
}

/**
 * Converts markdown a user typed to display HTML. Returns an empty string when there is nothing to
 * show; falls back to the escaped source if marked throws, so a malformed remark still reads.
 *
 * @param {any} pMarkdown - the remark source.
 * @param {any} [pLog] - optional fable log to report a parse failure to.
 * @return {String}
 */
function renderUserMarkdown(pMarkdown, pLog)
{
	if ((pMarkdown === null) || (typeof(pMarkdown) === 'undefined') || (pMarkdown === ''))
	{
		return '';
	}
	const tmpMarkdown = (typeof(pMarkdown) === 'string') ? pMarkdown : String(pMarkdown);
	try
	{
		return String(getUserContentMarked().parse(tmpMarkdown));
	}
	catch (pError)
	{
		// Escaped source is a safe degradation -- the remark still reads, just unformatted -- so this
		// never takes a screen down. It is reported once so it is findable in a console full of noise.
		if (pLog && !_MarkedUnusableReported)
		{
			_MarkedUnusableReported = true;
			pLog.error(`Markdown remarks are rendering as plain text: ${pError.message}`, pError);
		}
		return escapeHTML(tmpMarkdown);
	}
}

module.exports = { renderUserMarkdown: renderUserMarkdown, escapeHTML: escapeHTML, safeURL: safeURL, safeImageURL: safeImageURL, RemarksCSS: RemarksCSS, updateRemarkTruncation: updateRemarkTruncation, watchRemarksTruncation: watchRemarksTruncation };
